import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Plus, FileSpreadsheet, FileText, Ban, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { comprasRepuestosApi, cajaChicaApi, busesApi, stockApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';
import { coincideProducto } from '../lib/buscar';
import BusSelect from '../components/BusSelect';
import AnularModal from '../components/AnularModal';

// Caja Repuestos (2026-10-07, pedido de Rogelio): registro aparte de lo que se gasta en
// comprar repuestos/piezas, ademas del stock. Cada compra sale de la Caja Repuestos.

const fmt = (n: number) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const METODOS = [{ v: 'efectivo', l: 'Efectivo' }, { v: 'yape_plin', l: 'Yape / Plin' }, { v: 'transferencia', l: 'Transferencia' }];

function rangoPeriodo(p: 'hoy' | 'semana' | 'quincena' | 'mes', hoy: string): [string, string] {
  const [a, m, d] = hoy.split('-').map(Number);
  const iso = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  if (p === 'hoy') return [hoy, hoy];
  if (p === 'semana') { const r = new Date(a, m - 1, d); const l = new Date(r); l.setDate(r.getDate() - ((r.getDay() + 6) % 7)); const dm = new Date(l); dm.setDate(l.getDate() + 6); return [iso(l), iso(dm)]; }
  if (p === 'quincena') return d <= 15 ? [iso(new Date(a, m - 1, 1)), iso(new Date(a, m - 1, 15))] : [iso(new Date(a, m - 1, 16)), iso(new Date(a, m, 0))];
  return [iso(new Date(a, m - 1, 1)), iso(new Date(a, m, 0))];
}

const vacio = { descripcion: '', cantidad: '1', monto: '', proveedor: '', metodo_pago: 'efectivo', bus_padron: '', observaciones: '', agregar_stock: false, producto_id: '', producto_txt: '' };

const CajaRepuestos = () => {
  const navigate = useNavigate();
  const hoy = fechaLima();
  const [periodo, setPeriodo] = useState<'hoy' | 'semana' | 'quincena' | 'mes'>('mes');
  const [desde, setDesde] = useState(rangoPeriodo('mes', hoy)[0]);
  const [hasta, setHasta] = useState(rangoPeriodo('mes', hoy)[1]);
  const [compras, setCompras] = useState<any[]>([]);
  const [fondo, setFondo] = useState<any>(null);
  const [buses, setBuses] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [form, setForm] = useState({ ...vacio });
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [paraAnular, setParaAnular] = useState<any>(null);

  const cargar = () => {
    comprasRepuestosApi.getAll({ desde, hasta }).then(setCompras).catch(() => toast.error('No se pudieron cargar las compras'));
    cajaChicaApi.getSaldo('ventas').then((r) => setFondo({ abierto: true, saldo: Number(r?.saldo) || 0 })).catch(() => {});
  };
  useEffect(cargar, [desde, hasta]);
  useEffect(() => {
    busesApi.getAll().then(setBuses).catch(() => {});
    stockApi.getAll().then((p) => setProductos((p || []).filter((x: any) => !x.eliminado))).catch(() => {});
  }, []);

  function elegirPeriodo(p: typeof periodo) { setPeriodo(p); const [a, b] = rangoPeriodo(p, hoy); setDesde(a); setHasta(b); }

  const sugeridos = useMemo(() => {
    const q = form.producto_txt.trim().toLowerCase();
    if (q.length < 2 || form.producto_id) return [];
    return productos.filter((p) => coincideProducto(q, p)).slice(0, 8);
  }, [form.producto_txt, form.producto_id, productos]);

  const total = compras.reduce((t, c) => t + Number(c.monto), 0);
  const agrupar = (clave: (c: any) => string) => {
    const m: Record<string, number> = {};
    compras.forEach((c) => { const k = clave(c) || '—'; m[k] = (m[k] || 0) + Number(c.monto); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const porProveedor = agrupar((c) => c.proveedor || 'Sin proveedor');
  const porBus = agrupar((c) => (c.bus_padron ? `Padrón ${c.bus_padron}` : 'Para almacén / sin bus'));

  async function guardar() {
    if (!form.descripcion.trim()) { toast.error('Escribe qué repuesto se compró'); return; }
    const monto = parseFloat(form.monto);
    if (!(monto > 0)) { toast.error('Escribe el monto total de la compra'); return; }
    if (form.agregar_stock && !form.producto_id) { toast.error('Elige el producto del stock al que se suma, o desmarca "agregar al stock"'); return; }
    if (fondo?.abierto && monto > fondo.saldo &&
        !confirm(`La compra (${fmt(monto)}) supera el saldo de Caja Repuestos (${fmt(fondo.saldo)}). ¿Registrarla igual? La caja quedará en negativo.`)) return;
    setGuardando(true);
    try {
      const r = await comprasRepuestosApi.create({
        descripcion: form.descripcion.trim(), cantidad: parseFloat(form.cantidad) || 1, monto, proveedor: form.proveedor.trim(),
        metodo_pago: form.metodo_pago, bus_padron: form.bus_padron || null, observaciones: form.observaciones.trim() || null,
        producto_id: form.producto_id || null, agregar_stock: form.agregar_stock,
      });
      toast.success(`Compra registrada. Saldo de Caja Repuestos: ${fmt(r.estado.saldo)}`);
      if (r.aviso) toast.warning(r.aviso);
      setForm({ ...vacio }); setAbierto(false); cargar();
    } catch (e: any) { toast.error(e?.message || 'No se pudo registrar la compra'); }
    finally { setGuardando(false); }
  }

  const titulo = `Compras de repuestos del ${desde.split('-').reverse().join('/')} al ${hasta.split('-').reverse().join('/')}`;
  const filas = () => compras.map((c) => [new Date(c.fecha).toLocaleDateString('es-PE'), c.descripcion, Number(c.cantidad), c.proveedor || '', c.bus_padron ? `Padrón ${c.bus_padron}` : '', c.agregado_stock ? 'Sí' : 'No', Number(c.monto).toFixed(2)]);
  function excel() {
    const aoa = [[titulo], [], ['Fecha', 'Repuesto', 'Cant.', 'Proveedor', 'Bus', 'Al stock', 'Monto'], ...filas(), [], ['TOTAL', '', '', '', '', '', total.toFixed(2)]];
    const ws = XLSX.utils.aoa_to_sheet(aoa); ws['!cols'] = [{ wch: 12 }, { wch: 44 }, { wch: 7 }, { wch: 22 }, { wch: 14 }, { wch: 9 }, { wch: 12 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Repuestos'); XLSX.writeFile(wb, `Caja_Repuestos_${desde}_${hasta}.xlsx`);
  }
  function pdf() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(13); doc.text('Caja Repuestos', 14, 14); doc.setFontSize(9); doc.text(`${titulo} · Total ${fmt(total)}`, 14, 20);
    autoTable(doc, { startY: 25, head: [['Fecha', 'Repuesto', 'Cant.', 'Proveedor', 'Bus', 'Al stock', 'Monto']], body: filas(),
      foot: [['TOTAL', '', '', '', '', '', total.toFixed(2)]], showFoot: 'lastPage', styles: { fontSize: 8 },
      headStyles: { fillColor: [107, 33, 168] }, footStyles: { fillColor: [243, 232, 255], textColor: 0 }, columnStyles: { 6: { halign: 'right' } } });
    doc.save(`Caja_Repuestos_${desde}_${hasta}.pdf`);
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><Wrench className="text-purple-600" /> Compras de repuestos</h1>
        <button onClick={() => setAbierto(true)} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold"><Plus className="w-4 h-4" /> Registrar compra</button>
      </div>
      <p className="text-sm text-gray-500 mb-2">Lo que se gasta en comprar repuestos y piezas. Cada compra sale de la Caja Repuestos.</p>
      <div className="mb-4 text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2">
        <span>¿Vas a registrar el <b>pago de una deuda</b> de un cliente? Eso no va aquí: se registra en <b>Créditos</b>, eligiendo al propietario.</span>
        <button onClick={() => navigate('/creditos')} className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-xs">Ir a Créditos</button>
      </div>

      {fondo?.abierto && (
        <div onClick={() => navigate('/caja-repuestos')} className="cursor-pointer flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 mb-4 text-sm">
          <PiggyBank className="w-4 h-4 text-purple-600" /> Caja Repuestos · saldo disponible <b className={fondo.saldo < 0 ? 'text-red-600' : 'text-purple-700'}>{fmt(fondo.saldo)}</b>
          <span className="ml-auto text-xs text-purple-700">Ver Caja Repuestos →</span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-4">
        {(['hoy', 'semana', 'quincena', 'mes'] as const).map((p) => (
          <button key={p} onClick={() => elegirPeriodo(p)} className={`px-3 py-2 rounded-lg text-sm ${periodo === p ? 'bg-purple-600 text-white' : 'bg-white border text-gray-600'}`}>
            {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Semana' : p === 'quincena' ? 'Quincena' : 'Mes'}</button>
        ))}
        <label className="text-xs text-gray-500">Desde<input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="block border rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">Hasta<input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="block border rounded-lg px-2 py-1.5 text-sm" /></label>
        <button onClick={excel} disabled={!compras.length} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border bg-white disabled:opacity-40"><FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel</button>
        <button onClick={pdf} disabled={!compras.length} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border bg-white disabled:opacity-40"><FileText className="w-4 h-4 text-red-600" /> PDF</button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow p-4"><div className="text-xs uppercase text-gray-500 font-semibold">Gastado en repuestos</div>
          <div className="text-3xl font-bold text-purple-700">{fmt(total)}</div><div className="text-xs text-gray-500">{compras.length} compra(s) en el periodo</div></div>
        <div className="bg-white rounded-lg shadow p-4"><div className="text-xs uppercase text-gray-500 font-semibold mb-1">Por proveedor</div>
          {porProveedor.length ? porProveedor.slice(0, 5).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span className="truncate pr-2">{k}</span><b className="font-mono">{fmt(v)}</b></div>) : <div className="text-sm text-gray-400">Sin compras</div>}</div>
        <div className="bg-white rounded-lg shadow p-4"><div className="text-xs uppercase text-gray-500 font-semibold mb-1">Por bus</div>
          {porBus.length ? porBus.slice(0, 5).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span className="truncate pr-2">{k}</span><b className="font-mono">{fmt(v)}</b></div>) : <div className="text-sm text-gray-400">Sin compras</div>}</div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
            <th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">Repuesto</th><th className="px-3 py-2 text-right">Cant.</th>
            <th className="px-3 py-2 text-left">Proveedor</th><th className="px-3 py-2 text-left">Bus</th><th className="px-3 py-2 text-left">Stock</th>
            <th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2"></th></tr></thead>
          <tbody className="divide-y">
            {compras.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No hay compras de repuestos en este periodo.</td></tr> :
              compras.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{new Date(c.fecha).toLocaleDateString('es-PE')}</td>
                  <td className="px-3 py-2">{c.descripcion}{c.observaciones && <div className="text-xs text-gray-400">{c.observaciones}</div>}</td>
                  <td className="px-3 py-2 text-right">{Number(c.cantidad)}</td>
                  <td className="px-3 py-2">{c.proveedor || '—'}</td>
                  <td className="px-3 py-2">{c.bus_padron ? `Padrón ${c.bus_padron}` : '—'}</td>
                  <td className="px-3 py-2 text-xs">{c.agregado_stock ? <span className="text-green-700">Sumado: {c.producto_nombre}</span> : '—'}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(c.monto)}</td>
                  <td className="px-3 py-2"><button onClick={() => setParaAnular(c)} className="text-red-600 text-xs underline flex items-center gap-1"><Ban className="w-3 h-3" /> Anular</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {abierto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6 space-y-3 max-h-[92vh] overflow-y-auto">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Wrench className="text-purple-600" /> Registrar compra de repuestos</h2>
            <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-sm text-purple-800">Sale de la <b>Caja Repuestos</b>{fondo?.abierto ? <> · saldo disponible <b>{fmt(fondo.saldo)}</b></> : null}.</div>
            <label className="block text-sm font-medium">Repuesto / pieza *<input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Ej: 2 pastillas de freno delanteras MB" className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium">Cantidad<input type="number" min="0" step="1" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
              <label className="block text-sm font-medium">Monto total (S/) *<input type="number" min="0" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium">Proveedor<input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} placeholder="Tienda o vendedor" className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
              <label className="block text-sm font-medium">Forma de pago<select value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2">{METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></label>
            </div>
            <div className="text-sm font-medium">Bus de destino (opcional)
              <div className="mt-1"><BusSelect buses={buses} value={form.bus_padron} onChange={(v) => setForm({ ...form, bus_padron: v })} placeholder="Escribe el padrón si es para un bus…" /></div></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.agregar_stock} onChange={(e) => setForm({ ...form, agregar_stock: e.target.checked })} /> Agregar también al stock (almacén)</label>
            {form.agregar_stock && (
              <div className="relative">
                <input value={form.producto_txt} onChange={(e) => setForm({ ...form, producto_txt: e.target.value, producto_id: '' })} placeholder="Busca el producto del stock (nombre o código)" className="w-full border rounded-lg px-3 py-2 text-sm" />
                {form.producto_id && <div className="text-xs text-green-700 mt-1">Se sumarán {form.cantidad || 1} unidad(es) a este producto.</div>}
                {sugeridos.length > 0 && (
                  <ul className="absolute z-[100] mt-1 w-full bg-white border rounded-lg shadow max-h-56 overflow-auto text-sm">
                    {sugeridos.map((p) => <li key={p.id} onClick={() => setForm({ ...form, producto_id: p.id, producto_txt: p.nombre })} className="px-3 py-1.5 hover:bg-purple-50 cursor-pointer">{p.nombre} <span className="text-gray-400 text-xs">stock {p.stock}</span></li>)}
                  </ul>
                )}
              </div>
            )}
            <label className="block text-sm font-medium">Observaciones<textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setAbierto(false)} className="px-4 py-2 rounded-lg border">Cancelar</button>
              <button onClick={guardar} disabled={guardando} className="px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold disabled:opacity-50">Registrar compra</button>
            </div>
          </div>
        </div>
      )}

      {paraAnular && (
        <AnularModal
          titulo="Anular compra de repuestos"
          descripcion={`Se anulará la compra "${paraAnular.descripcion}" de ${fmt(paraAnular.monto)}. El dinero vuelve a la Caja Repuestos${paraAnular.agregado_stock ? ' y se resta del stock lo que se había sumado' : ''}.`}
          textoBoton="Anular compra"
          onClose={() => setParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await comprasRepuestosApi.anular(paraAnular.id, { clave, motivo });
            toast.success('Compra anulada'); setParaAnular(null); cargar();
          }}
        />
      )}
    </div>
  );
};

export default CajaRepuestos;
